import { lazy, Suspense, useEffect, useRef, useState, type FormEvent } from 'react';
import { ArrowLeft } from 'lucide-react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router';
import { Alert, Button, Field, Input } from '@/components/ui';
import { errorMessage } from '@/lib/api/query';
import { requestOtp, verifyOtp, type OtpRequestResult } from '@/lib/auth/api';
import { useAuth } from '@/lib/auth/AuthProvider';
import { AUDIENCE_HOME, audienceForRole, type Audience } from '@/lib/auth/roles';
import { IS_DEMO } from '@/lib/env';
import { formatPhone, isValidIndianMobile, toTenDigitPhone } from '@/lib/format';
import { AuthLayout } from './AuthLayout';

// Demo build only: `IS_DEMO` is a build-time constant, so the live bundle never includes this chunk.
const DemoAccounts = IS_DEMO ? lazy(() => import('@/demo/DemoAccounts')) : null;

const copy: Record<Audience, { title: string; subtitle: string; notRegistered: string }> = {
  owner: {
    title: 'Owner & manager sign in',
    subtitle: 'Use the mobile number registered on your TCM account.',
    notRegistered: 'This number isn’t registered as a center owner or manager account.',
  },
  student: {
    title: 'Student sign in',
    subtitle: 'Use the mobile number your center registered for you.',
    notRegistered: 'This number isn’t registered as a student account.',
  },
  parent: {
    title: 'Parent sign in',
    subtitle: 'Use the mobile number you gave the center at admission.',
    notRegistered: 'This number isn’t registered as a parent account.',
  },
  internal: {
    title: 'TCM team sign in',
    subtitle: 'Restricted to TCM staff. Access is logged.',
    notRegistered: 'This number doesn’t have access to the TCM console.',
  },
};

const RESEND_SECONDS = 30;

export function LoginPage({ audience }: { audience: Audience }) {
  const { session, signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState<'phone' | 'otp'>('phone');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [otpError, setOtpError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [devHint, setDevHint] = useState<OtpRequestResult['testOtp']>();
  const [secondsLeft, setSecondsLeft] = useState(0);
  const otpRef = useRef<HTMLInputElement>(null);
  const text = copy[audience];

  useEffect(() => {
    if (secondsLeft <= 0) {
      return;
    }
    const id = window.setTimeout(() => setSecondsLeft((value) => value - 1), 1000);
    return () => window.clearTimeout(id);
  }, [secondsLeft]);

  useEffect(() => {
    if (step === 'otp') {
      otpRef.current?.focus();
    }
  }, [step]);

  if (session) {
    return <Navigate to={AUDIENCE_HOME[session.audience]} replace />;
  }

  const sendCode = async (): Promise<boolean> => {
    setFormError(null);
    if (!isValidIndianMobile(phone)) {
      setPhoneError('Enter a valid 10-digit mobile number.');
      return false;
    }
    setPhoneError(null);
    setSubmitting(true);
    try {
      const result = await requestOtp(phone);
      setDevHint(result.testOtp);
      setSecondsLeft(RESEND_SECONDS);
      return true;
    } catch (error) {
      setFormError(errorMessage(error));
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const onPhoneSubmit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (await sendCode()) {
      setOtp('');
      setStep('otp');
    }
  };

  const onOtpSubmit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setFormError(null);
    if (!/^\d{6}$/.test(otp)) {
      setOtpError('Enter the 6-digit code.');
      return;
    }
    setOtpError(null);
    setSubmitting(true);
    try {
      const result = await verifyOtp(phone, otp);
      const resolved = audienceForRole(result.user.role);
      if (resolved !== audience) {
        setFormError(text.notRegistered);
        return;
      }
      signIn({ accessToken: result.accessToken, user: result.user, audience });
      const from = (location.state as { from?: string } | null)?.from;
      const base = AUDIENCE_HOME[audience];
      void navigate(from && (from === base || from.startsWith(`${base}/`) || from.startsWith(`${base}?`)) ? from : base, { replace: true });
    } catch (error) {
      setOtpError(errorMessage(error, 'That code didn’t work. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout>
      <Link to="/login" className="mb-6 inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-900">
        <ArrowLeft className="size-4" aria-hidden />
        All sign-in options
      </Link>
      <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">{text.title}</h1>
      <p className="mt-2 text-slate-500">{step === 'phone' ? text.subtitle : `We sent a 6-digit code to ${formatPhone(`+91${toTenDigitPhone(phone)}`)}.`}</p>

      {DemoAccounts && (
        <div className="mt-6">
          <Suspense fallback={null}>
            <DemoAccounts audience={audience} />
          </Suspense>
        </div>
      )}

      <div className="mt-8">
        {step === 'phone' ? (
          <form onSubmit={(event) => void onPhoneSubmit(event)} noValidate className="space-y-5">
            <Field label="Mobile number" error={phoneError} required>
              <div className="flex">
                <span className="inline-flex h-10 items-center rounded-l-lg border border-r-0 border-slate-300 bg-slate-50 px-3 text-sm font-semibold text-slate-600">+91</span>
                <Input
                  type="tel"
                  inputMode="numeric"
                  autoComplete="tel-national"
                  maxLength={10}
                  placeholder="98765 43210"
                  value={phone}
                  onChange={(event) => setPhone(event.target.value.replace(/\D/g, ''))}
                  className="rounded-l-none"
                />
              </div>
            </Field>
            {formError && <Alert tone="danger">{formError}</Alert>}
            <Button type="submit" size="lg" className="w-full" loading={submitting}>
              Send code
            </Button>
          </form>
        ) : (
          <form onSubmit={(event) => void onOtpSubmit(event)} noValidate className="space-y-5">
            <Field label="Verification code" error={otpError} required>
              <Input
                ref={otpRef}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                placeholder="• • • • • •"
                value={otp}
                onChange={(event) => setOtp(event.target.value.replace(/\D/g, ''))}
                className="text-center font-display text-xl tracking-[0.5em]"
              />
            </Field>
            {(import.meta.env.DEV || IS_DEMO) && devHint && (
              <Alert tone="info" title="Development sign-in">
                This server is in development mode. The code is <strong className="tabular">{devHint}</strong>.{' '}
                <button type="button" className="font-semibold underline" onClick={() => setOtp(devHint)}>
                  Fill it in
                </button>
              </Alert>
            )}
            {formError && <Alert tone="danger">{formError}</Alert>}
            <Button type="submit" size="lg" className="w-full" loading={submitting}>
              Verify and continue
            </Button>
            <div className="flex items-center justify-between text-sm">
              <button type="button" onClick={() => setStep('phone')} className="font-medium text-slate-500 hover:text-slate-900">
                Change number
              </button>
              <button
                type="button"
                disabled={secondsLeft > 0 || submitting}
                onClick={() => void sendCode()}
                className="font-semibold text-brand-700 hover:text-brand-900 disabled:cursor-not-allowed disabled:text-slate-400"
              >
                {secondsLeft > 0 ? `Resend in ${secondsLeft}s` : 'Resend code'}
              </button>
            </div>
          </form>
        )}
      </div>
    </AuthLayout>
  );
}
